import React, { useState, useEffect } from 'react';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  getDictMappingList,
  saveDictMapping,
  type DictMappingItem,
  type DictMappingEntry,
} from '../../services/data-integration-api';

interface DictMappingDialogProps {
  connectionId: string;
  sourceDictKey: string;
  onClose: () => void;
  onSaved?: () => void;
}

export default function DictMappingDialog({ connectionId, sourceDictKey, onClose, onSaved }: DictMappingDialogProps) {
  const [entries, setEntries] = useState<DictMappingEntry[]>([]);
  const [existingMappings, setExistingMappings] = useState<DictMappingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const result = await getDictMappingList({ connectionId, source_dict_key: sourceDictKey });
        setExistingMappings(result.items);
        if (result.items.length > 0) {
          setEntries(result.items.map(item => ({
            source_dict_value: item.source_dict_value,
            source_dict_label: item.source_dict_label,
            local_dict_key: item.local_dict_key,
            local_dict_value: item.local_dict_value,
            local_dict_label: item.local_dict_label,
          })));
        } else {
          setEntries([{ source_dict_value: '', local_dict_key: '', local_dict_value: '' }]);
        }
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [connectionId, sourceDictKey]);

  const updateEntry = (index: number, field: string, value: string) => {
    const updated = [...entries];
    (updated[index] as any)[field] = value;
    setEntries(updated);
  };

  const addRow = () => {
    setEntries([...entries, { source_dict_value: '', local_dict_key: '', local_dict_value: '' }]);
  };

  const removeRow = (index: number) => {
    setEntries(entries.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    const validEntries = entries.filter(e => e.source_dict_value && e.local_dict_key && e.local_dict_value);
    if (validEntries.length === 0) {
      setError('至少填写一条有效的对照关系');
      return;
    }

    setSaving(true);
    setError('');
    try {
      await saveDictMapping({
        connectionId,
        source_dict_key: sourceDictKey,
        mappings: validEntries,
      });
      onSaved?.();
      onClose();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-auto">
        <DialogHeader>
          <DialogTitle>字典对照绑定</DialogTitle>
          <DialogDescription>
            字典标识: <code className="font-mono">{sourceDictKey}</code>
          </DialogDescription>
        </DialogHeader>

        {error && <p className="text-destructive text-sm">{error}</p>}

        {loading ? (
          <p className="text-muted-foreground">加载中...</p>
        ) : (
          <>
            <Table className="mb-3">
              <TableHeader>
                <TableRow>
                  <TableHead>源值</TableHead>
                  <TableHead>源名称</TableHead>
                  <TableHead>本系统字典标识</TableHead>
                  <TableHead>本系统值</TableHead>
                  <TableHead>本系统名称</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((entry, i) => (
                  <TableRow key={i}>
                    <TableCell className="p-1">
                      <Input className="h-8" value={entry.source_dict_value} onChange={e => updateEntry(i, 'source_dict_value', e.target.value)} />
                    </TableCell>
                    <TableCell className="p-1">
                      <Input className="h-8" value={entry.source_dict_label || ''} onChange={e => updateEntry(i, 'source_dict_label', e.target.value)} />
                    </TableCell>
                    <TableCell className="p-1">
                      <Input className="h-8" value={entry.local_dict_key} onChange={e => updateEntry(i, 'local_dict_key', e.target.value)} />
                    </TableCell>
                    <TableCell className="p-1">
                      <Input className="h-8" value={entry.local_dict_value} onChange={e => updateEntry(i, 'local_dict_value', e.target.value)} />
                    </TableCell>
                    <TableCell className="p-1">
                      <Input className="h-8" value={entry.local_dict_label || ''} onChange={e => updateEntry(i, 'local_dict_label', e.target.value)} />
                    </TableCell>
                    <TableCell className="p-1">
                      <Button variant="ghost" size="sm" onClick={() => removeRow(i)}>
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <DialogFooter>
              <Button variant="outline" onClick={addRow}>添加行</Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? '保存中...' : '批量保存'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
